<template>
  <div class="ic-billing-history">
    <div class="ic-billing-history__container">
      <div class="ic-billing-history__header">
        <h1 class="ic-billing-history__header-title">{{ pageTitle }}</h1>
        <router-link
          :to="{ name: 'dashboard-account' }"
          class="ic-billing-history__header-link"
        >
          {{ accountLinkTitle }}
        </router-link>
      </div>
      <tz-spinner
        v-if="loadingItems"
        :is-check="false"
        spinner-size="140"
        :spinner-animation="true"
        spinner-color="#4476ff"
        spinner-stroke="2"
        :spinner-delay="1000"
        :is-starting-spin="true"
      />
      <div v-else class="ic-billing-history__content">
        <client-only v-if="pageTransactions.length">
          <div class="ic-table">
            <div v-if="!isMobile" class="ic-table__column-headers">
              <div v-for="(header, index) in columnHeaders" :key="index">
                {{ header }}
              </div>
            </div>
            <div class="ic-table__rows">
              <div
                v-for="(transaction, transactionIndex) in pageTransactions"
                class="ic-table__row"
                v-touch:tap="() => toggleExpansion(transactionIndex)"
              >
                <div
                  class="ic-table__row-field"
                  v-for="(transactionItem, index) in transaction"
                >
                  <span v-if="isMobile" class="ic-table__item-header">{{
                    columnHeaders[index]
                  }}</span>
                  <div
                    v-if="Array.isArray(transactionItem)"
                    class="ic-table__item-receipt"
                  >
                    <div class="ic-table__item-receipt-heading">
                      <p class="ic-table__item-receipt-heading-key">
                        {{ transactionItem[0].key }}
                      </p>
                      <p
                        class="ic-table__item-receipt-heading-value"
                        :class="{
                          'ic-table__item-receipt-heading-value--active':
                            transactionIndex === currentExpandedIndex
                        }"
                      >
                        {{ transactionItem[0].value }}
                      </p>
                    </div>
                    <div
                      class="'ic-table__item-receipt-rows"
                      v-if="transactionIndex === currentExpandedIndex"
                    >
                      <div
                        v-for="receiptItem in transactionItem.slice(1)"
                        class="ic-table__item-receipt-row"
                      >
                        <p class="ic-table__item-receipt-row-key">
                          {{ receiptItem.key }}
                        </p>
                        <p class="ic-table__item-receipt-row-value">
                          {{ receiptItem.value }}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div v-else>{{ transactionItem }}</div>
                </div>
              </div>
            </div>
            <tz-paginate
              :base-url="baseUrl"
              :max-pages="maxPages"
              :items-per-page="itemsPerPage"
              :total-items="this.transactionsCount"
              :current-page="currentPage"
              :has-trailing-slash="true"
              :use-router-links="true"
              :show-arrow-buttons="true"
            />
          </div>
        </client-only>
        <div v-else-if="!loadingItems">
          <p>{{ noBillingHistoryMessage }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TzPaginate from '@trazi/tz-paginate/src/tz-paginate.vue';
import TzSpinner from '@trazi/tz-spinner/src/tz-spinner.vue';
import billingSDK from '@/assets/js/shared/services/billing-sdk';
import billingProcessorSDK from '@/assets/js/shared/services/billing-processor-sdk';
import Meta from '@/assets/js/shared/misc/meta.mjs';
import setTitleCase from '@/assets/js/shared/helpers/setTitleCase';
import getMobileQuery from '@/assets/js/shared/helpers/getMobileQuery';
import getPaypalEmail from '@/assets/js/shared/helpers/getPaypalEmail';
import { mapGetters } from 'vuex';

const pageCms = require(`@/assets/cms/pages/dashboard/dashboard-billing-history.json`);
const mobileMediaQuery = getMobileQuery({ width: 990 });

const {
  transaction_date: dateColumnTitle,
  transaction_type: typeColumnTitle,
  transaction_billed_to: billedToColumnTitle,
  transaction_total: totalColumnTitle,
  transaction_status: statusColumnTitle
} = pageCms.billing_history_table_column_headings;

export default {
  name: 'dashboard-billing-history',
  layout: 'dashboard',
  components: {
    TzPaginate,
    TzSpinner
  },
  data() {
    return {
      pageCms,
      isMobile: this.setIsMobile,
      pageTitle: pageCms.billing_history_header.page_title,
      accountLinkTitle: pageCms.billing_history_header.account_link_title,
      noBillingHistoryMessage: pageCms.no_billing_history_message,
      maxPages: pageCms.pagination.max_pages,
      itemsPerPage: pageCms.pagination.items_per_page,
      currentPage: this.$route.params.page ? +this.$route.params.page : 1,
      baseUrl: `/dashboard/account/billing-history/`,
      columnHeaders: [
        dateColumnTitle,
        statusColumnTitle,
        typeColumnTitle,
        billedToColumnTitle,
        totalColumnTitle
      ],
      pageTransactions: [],
      loadingItems: true,
      currentExpandedIndex: null,
      payments: null
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser'])
  },
  methods: {
    getStatusLabel(status) {
      let updatedStatus;

      switch (status) {
        case 'AUTHORIZED':
          updatedStatus = 'Pending';
          break;
        case 'PARTIAL-CAPTURE':
          updatedStatus = 'Processed';
          break;
        case 'CAPTURED':
          updatedStatus = 'Processed';
          break;
        case 'SUBSTITUTE':
          updatedStatus = 'Upgraded';
          break;
        default:
          updatedStatus = setTitleCase({ text: status });
      }

      return updatedStatus;
    },
    setIsMobile(e) {
      this.isMobile = e.matches;
    },
    toggleExpansion(index) {
      if (index === this.currentExpandedIndex) {
        this.currentExpandedIndex = null;
        return;
      }
      this.currentExpandedIndex = index;
    },
    async setPayments(userDomain, userId) {
      try {
        let result;
        // Determine Payments endpoint logic based on SDK version
        const isV2 =
          this.currentUser &&
          this.currentUser.user_data &&
          this.currentUser.user_data.v2;
        if (isV2) {
          result = await this.billingProcessorSDK.PaymentList.byAccount(
            userDomain,
            userId
          );
        } else {
          result = await this.billingSDK.Payments.byAccount(userDomain, userId);
        }
        if (result && result.type && result.type.includes('error')) {
          this.payments = [];
        } else if (Array.isArray(result)) {
          this.payments = result;
        } else {
          this.payments = [];
        }
      } catch (err) {
        console.error(err);
        this.payments = [];
        this.$bugsnag?.notify(err);
      }
    },
    async getPaypalBillTo(userDomain, userId, transaction) {
      if (!this.payments) {
        await this.setPayments(userDomain, userId);
      }
      if (!this.payments.length) {
        return 'Unknown';
      }
      for (let j = 0; j < this.payments.length; j++) {
        const payment = this.payments[j];
        if (payment.id === transaction.payment_id) {
          return payment.name
            ? `PayPal - ${getPaypalEmail({ paypalPayment: payment })}`
            : 'PayPal';
        }
      }
      return 'Unknown';
    },
    async getPageTransactions(userId, userDomain) {
      const numRowsToSkip = (this.currentPage - 1) * this.itemsPerPage;
      const transactionsForTable = [];
      try {
        const transactions = await this.billingSDK.Transactions.byAccount(
          userDomain,
          userId,
          this.itemsPerPage,
          null,
          numRowsToSkip,
          null,
          null,
          null,
          null,
          'status=CAPTURED||status=AUTHORIZED||status=CANCELED||status=GIFTED||status=PARTIAL-CAPTURE||status=REFUNDED||status=PROCESSING||status=SUBSTITUTE||status=VOIDED',
          'created_at',
          true
        );
        for (let i = 0; i < transactions.length; i++) {
          const transaction = transactions[i];
          let purchaseBillTo;
          // Determine PayPal logic based on SDK version
          const isV2 =
            this.currentUser &&
            this.currentUser.user_data &&
            this.currentUser.user_data.v2;
          const isPaypal = isV2
            ? transaction.type && transaction.type.toLowerCase() === 'paypal'
            : transaction.kind && transaction.kind.toLowerCase() === 'paypal';
          if (!transaction.kind || transaction.kind === 'UNKNOWN') {
            // unknown kind of transaction
            purchaseBillTo = 'Unknown';
          } else if (
            isPaypal ||
            (transaction.kind && transaction.kind.toLowerCase() === 'sprel')
          ) {
            // paypal transaction
            purchaseBillTo = await this.getPaypalBillTo(
              userDomain,
              userId,
              transaction
            );
          } else {
            // credit card transaction
            purchaseBillTo = `${setTitleCase({
              text: transaction.type?.replace('_', ' ')
            })} - ${this.cardLastFour(transaction)
              .toString()
              .padStart(4, '0')}`;
          }
          const transactionDate = new Date(transaction.created_at);
          const m = transactionDate.getMonth() + 1;
          const d = transactionDate.getDate();
          const yyyy = transactionDate.getFullYear();
          const purchaseDate = `${m}/${d}/${yyyy}`;
          const transactionTotal = `$${Number(
            transaction.capture_amount
          ).toFixed(2)}`;
          const status = this.getStatusLabel(transaction.status);
          const total = {
            key: 'Total',
            value: transactionTotal
          };
          const receiptInfo = [
            {
              key: transaction.confirmation_id ?? transaction.id,
              value: ''
            }
          ];
          transaction.line_items.forEach(lineItem => {
            const key = `${lineItem.name}`;
            const value = `$${lineItem.total.toFixed(2)}`;
            receiptInfo.push({ key, value });
          });
          receiptInfo.push(total);
          const purchase = [
            purchaseDate,
            status,
            receiptInfo,
            purchaseBillTo,
            transactionTotal
          ];
          transactionsForTable.push(purchase);
        }
      } catch (err) {
        console.error(err);
        this.$bugsnag?.notify(err);
      }
      return transactionsForTable;
    },
    /**
     * Returns the last four digits of the card number
     * @param payment
     * @returns {string|null}
     */
    cardLastFour(payment) {
      const hasVirtualCard =
        payment?.type?.includes('apple') || payment?.type?.includes('google');
      const realLastFour =
        payment?.name?.split(' ')[1] || payment?.extra_tracking?.realLastFour;
      const lastFour = payment?.card_check_digit;

      return hasVirtualCard ? realLastFour : lastFour;
    }
  },
  async mounted() {
    try {
      this.billingSDK = this.currentUser.user_data?.v2
        ? this.$billingV2SDK
        : billingSDK(window).Billing;

      this.billingProcessorSDK = billingProcessorSDK(window);
      this.transactionsCount = await this.billingSDK.Transactions.countByAccount(
        this.currentUser.domain,
        this.currentUser.id,
        null,
        null,
        null,
        null,
        null,
        null,
        'status=CAPTURED||status=AUTHORIZED||status=CANCELED||status=GIFTED||status=PARTIAL-CAPTURE||status=REFUNDED||status=PROCESSING||status=SUBSTITUTE||status=VOIDED'
      );

      this.pageTransactions = await this.getPageTransactions(
        this.currentUser.id,
        this.currentUser.domain
      );

      if (!this.pageTransactions.length) {
        await this.$router.push({ name: 'dashboard-billing-history' });
      }

      this.loadingItems = false;
    } catch (err) {
      console.error(err);
      this.$bugsnag?.notify(err);
    }

    try {
      // Chrome & Firefox
      mobileMediaQuery.addEventListener('change', this.setIsMobile);
    } catch (err) {
      this.$bugsnag?.notify(err);
      // Safari
      mobileMediaQuery.addListener(this.setIsMobile);
    }
    this.isMobile = mobileMediaQuery.matches;
  },
  beforeDestroy() {
    mobileMediaQuery.removeEventListener('change', this.setIsMobile);
  },
  props: {
    cms: {
      type: Object,
      default: () => {
        return {
          global: require(`@/assets/cms/global.json`)
        };
      }
    }
  },
  head() {
    return {
      title: this.pageCms.meta_title,
      meta: this.pageCms.meta ? Meta.convertToNuxtMeta(this.pageCms.meta) : []
    };
  }
};
</script>
